import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { collection, doc, getDoc, getDocs, getFirestore, query, where } from "firebase/firestore";
import { computeBookingSizeMultiplier } from "../player/Utils";
import "./BookingMock.css";

function normalizeHex(color, fallback) {
  const raw = String(color || "").trim();
  if (/^#?[0-9a-fA-F]{6}$/.test(raw)) {
    return raw.startsWith("#") ? raw.toUpperCase() : "#" + raw.toUpperCase();
  }
  if (/^#?[0-9a-fA-F]{3}$/.test(raw)) {
    const h = raw.replace("#", "");
    return ("#" + h.split("").map((c) => c + c).join("")).toUpperCase();
  }
  return fallback;
}

function hexToRgb(hex) {
  const value = parseInt(hex.slice(1), 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

function relativeLuminance(hex) {
  const { r, g, b } = hexToRgb(hex);
  const linear = [r, g, b].map((channel) => {
    const s = channel / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * linear[0] + 0.7152 * linear[1] + 0.0722 * linear[2];
}

function isLightColor(hex) {
  return relativeLuminance(hex) > 0.45;
}

function withAlpha(hex, alpha) {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function contrastOn(hex) {
  return isLightColor(hex) ? "#1A1A1A" : "#FFFFFF";
}

function colorFromQueryParam(value) {
  const raw = String(value || "").trim();
  if (!raw) {
    return null;
  }
  return normalizeHex(raw.startsWith("#") ? raw : `#${raw}`, "");
}

async function resolveStory(storyId) {
  const db = getFirestore();
  const exactRef = doc(db, "stories", storyId);
  const exact = await getDoc(exactRef);
  if (exact.exists()) {
    return { id: exact.id, data: exact.data() };
  }

  const lower = storyId.toLowerCase();
  const stories = collection(db, "stories");
  for (const field of ["previousAliasLowerCase", "aliasLowerCase"]) {
    const snap = await getDocs(query(stories, where(field, "==", lower)));
    if (!snap.empty) {
      const found = snap.docs[0];
      return { id: found.id, data: found.data() };
    }
  }
  return null;
}

function IconFork() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M8.1 13.34 10.93 10.5 3.91 3.5a4 4 0 0 0 0 5.66l4.19 4.18Zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.47-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L9.7 14.25l.79.8 4.39-4.52Z" />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M19 4h-1V2h-2v2H8V2H6v2H5c-1.11 0-1.99.9-1.99 2L3 20a2 2 0 0 0 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2Zm0 16H5V10h14v10Zm0-12H5V6h14v2Z" />
    </svg>
  );
}

function IconClock() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2ZM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8Zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7Z" />
    </svg>
  );
}

function IconChevron() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="currentColor" d="M16.59 8.59 12 13.17 7.41 8.59 6 10l6 6 6-6z" />
    </svg>
  );
}

function BookingMock() {
  const { t, i18n } = useTranslation();
  const { storyId } = useParams();
  const searchParams = useMemo(() => new URLSearchParams(window.location.search), []);
  const sizeMultiplierOverride = searchParams.get("sizeMultiplier");
  const urlDisplayName = String(searchParams.get("displayName") || "").trim();
  const urlLng = String(searchParams.get("lng") || "").trim();
  const urlSecondaryColor = colorFromQueryParam(searchParams.get("secondaryColor"));
  const urlPrimaryColor = colorFromQueryParam(searchParams.get("primaryColor"));
  const [status, setStatus] = useState("loading");
  const [story, setStory] = useState(null);
  const [sizeMultiplier, setSizeMultiplier] = useState(() =>
    computeBookingSizeMultiplier(window.innerWidth, sizeMultiplierOverride)
  );

  useEffect(() => {
    if (!urlLng) return;
    const normalized = urlLng.toLowerCase().startsWith("fr") ? "fr" : "en";
    if (i18n.language !== normalized) {
      i18n.changeLanguage(normalized);
    }
  }, [urlLng, i18n]);

  useEffect(() => {
    function updateSizeMultiplier() {
      setSizeMultiplier(computeBookingSizeMultiplier(window.innerWidth, sizeMultiplierOverride));
    }

    window.addEventListener("resize", updateSizeMultiplier);
    return () => window.removeEventListener("resize", updateSizeMultiplier);
  }, [sizeMultiplierOverride]);

  const background = normalizeHex(story?.secondaryColor || urlSecondaryColor, "#FFFFFF");
  const primary = normalizeHex(story?.primaryColor || urlPrimaryColor, "#2FC4B5");
  const text = contrastOn(background);
  const muted = withAlpha(text, 0.72);
  const border = withAlpha(text, 0.18);
  // Même logique que le CTA Réservation de RestaurantInfos (btn-cta)
  const btnBg = primary;
  const btnText = background;

  useLayoutEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.classList.add("booking-mock-html");
    body.classList.add("booking-mock-body");
    html.style.backgroundColor = background;
    body.style.backgroundColor = background;
    return () => {
      html.classList.remove("booking-mock-html");
      body.classList.remove("booking-mock-body");
      html.style.backgroundColor = "";
      body.style.backgroundColor = "";
    };
  }, [background]);

  useEffect(() => {
    let cancelled = false;
    const id = String(storyId || "").trim();
    if (!id) {
      setStatus("missing");
      return undefined;
    }

    resolveStory(id)
      .then((resolved) => {
        if (cancelled) return;
        if (!resolved) {
          setStatus("missing");
          return;
        }
        setStory(resolved.data);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [storyId]);

  const storyName =
    story?.posName ||
    story?.chainRestaurants?.[0]?.posName ||
    story?.alias ||
    "";
  const displayName =
    urlDisplayName ||
    storyName ||
    String(storyId || "").trim() ||
    "…";

  useEffect(() => {
    if (displayName && displayName !== "…") {
      document.title = displayName.toUpperCase() + " - Réservation en ligne";
    }
  }, [displayName]);

  useLayoutEffect(() => {
    if (window.parent === window) {
      return undefined;
    }
    const notifyReady = () => {
      window.parent.postMessage({ type: "upreality-booking-mock-ready" }, window.location.origin);
    };
    notifyReady();
    const retryId = window.setTimeout(notifyReady, 150);
    return () => window.clearTimeout(retryId);
  }, []);

  const welcomeText = (() => {
    if (status === "missing") return "Story introuvable";
    if (status === "error") return "Impossible de charger la réservation";
    return (
      <>
        Bienvenue au restaurant {displayName}
        <br />
        <br />
        À très bientôt
      </>
    );
  })();

  return (
    <div
      className="booking-mock"
      style={{
        "--booking-size-multiplier": sizeMultiplier,
        "--bm-bg": background,
        "--bm-text": text,
        "--bm-muted": muted,
        "--bm-border": border,
        "--bm-btn-bg": btnBg,
        "--bm-btn-text": btnText,
      }}
    >
      <div className="booking-mock-header">
        <div className="booking-mock-title">{displayName}</div>
        <div className="booking-mock-lang">{(i18n.language || "fr").toLowerCase().startsWith("fr") ? "FR" : "EN"}</div>
      </div>
      <div className="booking-mock-body">
        <div className="booking-mock-welcome">{welcomeText}</div>
        <div className="booking-mock-row">
          <div className="booking-mock-row-main">
            <IconFork />
            <span>2 couverts</span>
          </div>
          <IconChevron />
        </div>
        <div className="booking-mock-row">
          <div className="booking-mock-row-main">
            <IconCalendar />
            <span>Aujourd’hui</span>
          </div>
          <IconChevron />
        </div>
        <div className="booking-mock-row">
          <div className="booking-mock-row-main">
            <IconClock />
            <span className="booking-mock-row-label-muted">Horaire</span>
          </div>
          <IconChevron />
        </div>
        <div className="booking-mock-footer">
          <div className="booking-mock-btn-primary">Réserver</div>
          <div className="booking-mock-demo-note">
            <div className="booking-mock-demo-toast">
              <div className="booking-mock-demo-note-title">{t("ChainDemoVersionHint")}</div>
              <div className="booking-mock-demo-note-subtitle">
                {t("BookingMockDemoToastSubtitle")}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="booking-mock-blocker" aria-hidden="true" />
    </div>
  );
}

export default BookingMock;
